import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { FormattedMessage, useIntl } from "react-intl";
import ParkkiTheme from "themes/ParkkiTheme";
import Button from "components/Button";
import SelectField from "components/SelectField";
import { connect } from "react-redux";
import AutomaticSystemsActioner from "../../../../services/gates/AutomaticSystems/actioner";
import Abilities from "services/permissions/abilities";

const FormContainer = styled.div`
  padding: 20px 24px;
`;

const StatusSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const StatusSelectorLabel = styled.label`
  font-size: ${ParkkiTheme.fontSizes.small};
  font-weight: 500;
  color: ${ParkkiTheme.colors.darkGrey};
`;

const StatusSelectorRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const StatusSelectorWrapper = styled.div`
  flex: 1;
`;

const StatusValidateButton = styled(Button)`
  height: 40px;
  min-width: 120px;
  max-width: 120px;
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SelectorLabel = styled.label`
  font-size: ${ParkkiTheme.fontSizes.small};
  font-weight: 500;
  color: ${ParkkiTheme.colors.darkGrey};
`;

const SelectorRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const SelectorWrapper = styled.div`
  flex: 1;
`;

const ValidateButton = styled(Button)`
  height: 40px;
  min-width: 120px;
  max-width: 120px;
`;

function AutomaticSystemForm({
  system,
  systemDetails,
  automaticSystemsDetails,
  updateAutomaticSystemsMode,
  updateAutomaticSystemsStatus,
}) {
  const intl = useIntl();
  const [selectedMode, setSelectedMode] = useState(
    systemDetails?.operationMode?.name || "AUTO"
  );
  const [selectedStatus, setSelectedStatus] = useState("");

  const statusLoading =
    automaticSystemsDetails?.[system.id]?.statusLoading || false;
  const modeLoading =
    automaticSystemsDetails?.[system.id]?.modeLoading || false;

  useEffect(() => {
    if (systemDetails?.operationMode?.name) {
      setSelectedMode(systemDetails.operationMode.name);
    }
  }, [systemDetails]);

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleModeChange = (event) => {
    setSelectedMode(event.target.value);
  };

  const handleValidateMode = () => {
    updateAutomaticSystemsMode(system.id, {
      mode: selectedMode,
    });
  };

  const handleValidateStatus = () => {
    updateAutomaticSystemsStatus(system.id, {
      status: selectedStatus,
    });
  };

  const modeOptions = [
    {
      value: "AUTO",
      text: intl.formatMessage({ id: "automaticSystemsScene.modes.AUTO" }),
    },
    {
      value: "FREE",
      text: intl.formatMessage({ id: "automaticSystemsScene.modes.FREE" }),
    },
    {
      value: "BLOP",
      text: intl.formatMessage({ id: "automaticSystemsScene.modes.BLOP" }),
    },
    {
      value: "BLCL",
      text: intl.formatMessage({ id: "automaticSystemsScene.modes.BLCL" }),
    },
  ];

  const statusOptions = useMemo(() => {
    const options = [];

    if (Abilities.ability.can("read", "config_gates.automaticSystemsOpen")) {
      options.push({
        value: "OPEN",
        text: intl.formatMessage({ id: "automaticSystemsScene.form.open" }),
      });
    }

    if (Abilities.ability.can("read", "config_gates.automaticSystemsClose")) {
      options.push({
        value: "CLOSE",
        text: intl.formatMessage({ id: "automaticSystemsScene.form.close" }),
      });
    }

    if (
      Abilities.ability.can(
        "read",
        "config_gates.automaticSystemsEnableEmergency"
      )
    ) {
      options.push({
        value: "ENABLE_EMERGENCY",
        text: intl.formatMessage({
          id: "automaticSystemsScene.form.enableEmergency",
        }),
      });
      options.push({
        value: "DISABLE_EMERGENCY",
        text: intl.formatMessage({
          id: "automaticSystemsScene.form.disableEmergency",
        }),
      });
    }

    return options;
  }, [intl]);

  const canChangeStatus =
    Abilities.ability.can("read", "config_gates.automaticSystemsOpen") ||
    Abilities.ability.can("read", "config_gates.automaticSystemsClose") ||
    Abilities.ability.can(
      "read",
      "config_gates.automaticSystemsEnableEmergency"
    );

  const canChangeMode = Abilities.ability.can(
    "read",
    "config_gates.automaticSystemsMode"
  );

  return (
    <FormContainer>
      {canChangeStatus && statusOptions.length > 0 && (
        <StatusSelectorContainer>
          <StatusSelectorLabel>
            <FormattedMessage id="automaticSystemsScene.form.forceStatus" />
          </StatusSelectorLabel>
          <StatusSelectorRow>
            <StatusSelectorWrapper>
              <SelectField
                variant="outlined"
                label={intl.formatMessage({
                  id: "automaticSystemsScene.form.selectStatus",
                })}
                values={statusOptions}
                value={selectedStatus}
                onChange={handleStatusChange}
                fullWidth={true}
                size="small"
              />
            </StatusSelectorWrapper>
            <StatusValidateButton
              label={intl.formatMessage({
                id: "automaticSystemsScene.form.validate",
              })}
              color="primary"
              size="small"
              onClick={handleValidateStatus}
              fullWidth={false}
              disabled={statusLoading || modeLoading || !selectedStatus}
            />
          </StatusSelectorRow>
        </StatusSelectorContainer>
      )}

      {canChangeMode && (
        <SelectorContainer>
          <SelectorLabel>
            <FormattedMessage id="automaticSystemsScene.form.operationMode" />
          </SelectorLabel>
          <SelectorRow>
            <SelectorWrapper>
              <SelectField
                variant="outlined"
                label={intl.formatMessage({
                  id: "automaticSystemsScene.form.selectMode",
                })}
                values={modeOptions}
                value={selectedMode}
                onChange={handleModeChange}
                fullWidth={true}
                size="small"
              />
            </SelectorWrapper>
            <ValidateButton
              label={intl.formatMessage({
                id: "automaticSystemsScene.form.validate",
              })}
              color="primary"
              size="small"
              onClick={handleValidateMode}
              fullWidth={false}
              disabled={modeLoading || statusLoading || !selectedMode}
            />
          </SelectorRow>
        </SelectorContainer>
      )}
    </FormContainer>
  );
}

const mapStateToProps = (state) => ({
  automaticSystemsDetails: state.AutomaticSystems.automaticSystemsDetails,
});

const mapDispatchToProps = (dispatch) => ({
  updateAutomaticSystemsMode: (id, data) =>
    dispatch(AutomaticSystemsActioner.updateAutomaticSystemsMode(id, data)),
  updateAutomaticSystemsStatus: (id, data) =>
    dispatch(AutomaticSystemsActioner.updateAutomaticSystemsStatus(id, data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AutomaticSystemForm);
